/**
 * reportBlank.js — БЛАНК ЗАКЛЮЧЕНИЯ: постоянные настройки учреждения и
 * автосбор шапки из тегов исследования. [11.1.20]
 *
 * ПОВОД — образец настоящего заключения, присланный владельцем (МРТ, НМИЦ
 * онкологии): наша форма давала почти пустой лист, а в живом бланке есть
 * шапка учреждения с реквизитами, отделение, дата рождения, № истории
 * болезни, строка аппарата, перечень импульсных последовательностей и
 * подпись заведующего. Половина этого УЖЕ лежит в памяти программы —
 * просто никуда не подавалась.
 *
 * УСТРОЙСТВО. Здесь только данные и ЧИСТЫЕ функции: сборщики принимают
 * словарь тегов и список серий, возвращают строки. Ни DOM, ни импортов
 * ядра — как в wlChips/wlManual/printUtil: так их проверяет verify без
 * заглушек браузера. Хранение — localStorage напрямую (настройки бланка
 * это несколько килобайт текста, ради них не нужен движок storage.js с
 * сжатием и вытеснением; ключ назван явно и по-русски задокументирован).
 */

const КЛЮЧ = 'mikhmel_report_blank';
const КЛЮЧ_ПРОФИЛЕЙ = 'mikhmel_report_blanks';   // [11.1.26] несколько учреждений

/** Пустой бланк: ничего не выдумываем за владельца. */
export const ПУСТОЙ_БЛАНК = Object.freeze({
  учреждение: '',       // «федеральное государственное бюджетное учреждение …»
  реквизиты: '',        // адрес, телефоны, ОКПО/ОГРН/ИНН — свободный текст, 1–3 строки
  логотип: '',          // data:image/... ; пусто — печатается без логотипа
  отделения: {},        // { MR: 'ОТДЕЛЕНИЕ МАГНИТНО-РЕЗОНАНСНОЙ ТОМОГРАФИИ', CT: '…' }
  врачи: [],            // [{ фио: 'Иозефи Д.Я.', должность: 'заведующий отделением' }]
  // [11.1.30] Ниже — три просьбы владельца: выбор разделов, шрифты, логотип.
  разделы: {},          // { клинические:true, измерения:false, … } — пусто = все включены
  шрифты: {},           // { шапкаИмя, шапкаПт, телоИмя, телоПт }
  логотипВид: {},       // { ширинаМм: 22, место: 'слева'|'справа'|'сверху'|'нет' }
  поля: {},             // [11.1.35] { верхМм, низМм, левоМм, правоМм } — поля листа
});

// ══════════════════════════════════════════════════════════════
//  РАЗДЕЛЫ БЛАНКА [11.1.30]
//  Владелец: «одному заключению нужны Описание+Заключение, другому ещё и
//  Рекомендации». Раньше решала ПУСТОТА: не заполнил — не напечаталось.
//  Плохо тем, что решение принимал не врач: текст, оставшийся от прошлого
//  пациента, уезжал в документ молча. Теперь выбор явный — галочки, а текст
//  при этом НЕ теряется, просто не выводится.
// ══════════════════════════════════════════════════════════════

/** Ключ → подпись. «Заключение» в списке НЕТ намеренно: документ без него — не документ. */
export const РАЗДЕЛЫ_БЛАНКА = Object.freeze([
  { ключ: 'клинические',  подпись: 'Клинические данные' },
  { ключ: 'ип',           подпись: 'Строка «Исследование проведено в ИП»' },
  { ключ: 'описание',     подпись: 'Описание' },
  { ключ: 'измерения',    подпись: 'Измерения' },
  { ключ: 'сравнение',    подпись: 'Сравнение с предыдущим' },
  { ключ: 'рекомендации', подпись: 'Рекомендации' },
]);

export const НАБОРЫ_РАЗДЕЛОВ = Object.freeze({
  кратко: { клинические: false, ип: false, описание: true, измерения: false, сравнение: false, рекомендации: false },
  полный: { клинические: true, ип: true, описание: true, измерения: true, сравнение: true, рекомендации: true },
});

/** Пустая карта = все включены (совместимость со старыми профилями). */
export function разделВключён(разделы, ключ) {
  if (!разделы || !Object.keys(разделы).length) return true;
  return разделы[ключ] !== false;
}

// ══════════════════════════════════════════════════════════════
//  ШРИФТЫ И ЛОГОТИП [11.1.30]
// ══════════════════════════════════════════════════════════════

/** Только те, что есть и в Word, и в системе: подстановка «похожим» портит бланк. */
export const ШРИФТЫ = Object.freeze([
  'Times New Roman', 'Arial', 'Calibri', 'Georgia', 'Verdana', 'PT Astra Serif',
]);

export const ШРИФТЫ_ПО_УМОЛЧАНИЮ = Object.freeze({
  шапкаИмя: 'Times New Roman', шапкаПт: 12, телоИмя: 'Times New Roman', телоПт: 12,
});

export function шрифтыБланка(бланк = {}) {
  const ш = бланк.шрифты || {};
  const имя = (з, п) => (ШРИФТЫ.includes(з) ? з : п);
  const пт = (з, п) => { const v = Number(з); return Number.isFinite(v) && v >= 8 && v <= 18 ? v : п; };
  return {
    шапкаИмя: имя(ш.шапкаИмя, ШРИФТЫ_ПО_УМОЛЧАНИЮ.шапкаИмя),
    шапкаПт: пт(ш.шапкаПт, ШРИФТЫ_ПО_УМОЛЧАНИЮ.шапкаПт),
    телоИмя: имя(ш.телоИмя, ШРИФТЫ_ПО_УМОЛЧАНИЮ.телоИмя),
    телоПт: пт(ш.телоПт, ШРИФТЫ_ПО_УМОЛЧАНИЮ.телоПт),
  };
}

// ══════════════════════════════════════════════════════════════
//  ПОЛЯ ЛИСТА [11.1.35]
//  Просьба владельца: «в заключении нужно сделать поля 1,5 см с каждой
//  стороны, а ещё лучше внести возможность изменения полей в настройки».
//  Раньше поля были зашиты в двух местах и разными: печать — 18 мм со всех
//  сторон, DOCX — 20 мм и слева 25. Теперь число ОДНО, живёт в бланке,
//  правится в настройках и одинаково уходит и в печать, и в Word.
// ══════════════════════════════════════════════════════════════

export const ПОЛЯ_ПО_УМОЛЧАНИЮ = Object.freeze({ верхМм: 15, низМм: 15, левоМм: 15, правоМм: 15 });

/** Поля листа с зажимом 5…40 мм: нулевое поле съест текст принтером, огромное — лист. */
export function поляБланка(бланк = {}) {
  const п = бланк.поля || {};
  const мм = (з, умолч) => { const v = Number(з); return Number.isFinite(v) && v >= 5 && v <= 40 ? v : умолч; };
  return {
    верхМм: мм(п.верхМм, ПОЛЯ_ПО_УМОЛЧАНИЮ.верхМм),
    низМм: мм(п.низМм, ПОЛЯ_ПО_УМОЛЧАНИЮ.низМм),
    левоМм: мм(п.левоМм, ПОЛЯ_ПО_УМОЛЧАНИЮ.левоМм),
    правоМм: мм(п.правоМм, ПОЛЯ_ПО_УМОЛЧАНИЮ.правоМм),
  };
}

export const МЕСТА_ЛОГОТИПА = Object.freeze(['слева', 'справа', 'сверху', 'нет']);

export function логотипБланка(бланк = {}) {
  const л = бланк.логотипВид || {};
  const ш = Number(л.ширинаМм);
  return {
    ширинаМм: Number.isFinite(ш) && ш >= 8 && ш <= 60 ? ш : 22,
    место: МЕСТА_ЛОГОТИПА.includes(л.место) ? л.место : 'слева',
    есть: !!(бланк.логотип && л.место !== 'нет'),
  };
}

/** Название отделения по модальности — с разумной заготовкой. */
export const ОТДЕЛЕНИЕ_ПО_УМОЛЧАНИЮ = Object.freeze({
  MR: 'ОТДЕЛЕНИЕ МАГНИТНО-РЕЗОНАНСНОЙ ТОМОГРАФИИ',
  CT: 'ОТДЕЛЕНИЕ КОМПЬЮТЕРНОЙ ТОМОГРАФИИ',
  CR: 'РЕНТГЕНОЛОГИЧЕСКОЕ ОТДЕЛЕНИЕ',
  DX: 'РЕНТГЕНОЛОГИЧЕСКОЕ ОТДЕЛЕНИЕ',
  MG: 'ОТДЕЛЕНИЕ МАММОГРАФИИ',
  US: 'ОТДЕЛЕНИЕ УЛЬТРАЗВУКОВОЙ ДИАГНОСТИКИ',
  PT: 'ОТДЕЛЕНИЕ ПОЗИТРОННО-ЭМИССИОННОЙ ТОМОГРАФИИ',
  XA: 'ОТДЕЛЕНИЕ РЕНТГЕНОХИРУРГИЧЕСКИХ МЕТОДОВ ДИАГНОСТИКИ И ЛЕЧЕНИЯ',
});

export function прочитатьБланк() {
  try {
    const с = JSON.parse(localStorage.getItem(КЛЮЧ) || '{}');
    return { ...ПУСТОЙ_БЛАНК, ...с, отделения: { ...(с.отделения || {}) }, врачи: [...(с.врачи || [])] };
  } catch (e) { return { ...ПУСТОЙ_БЛАНК }; }
}

export function записатьБланк(б) {
  try { localStorage.setItem(КЛЮЧ, JSON.stringify({ ...ПУСТОЙ_БЛАНК, ...б })); return true; }
  catch (e) { return false; }   // приватный режим или переполнение
}

export function названиеОтделения(modality, бланк = {}) {
  const м = String(modality || '').toUpperCase().split(/[\\/]/)[0];
  return (бланк.отделения && бланк.отделения[м]) || ОТДЕЛЕНИЕ_ПО_УМОЛЧАНИЮ[м] || '';
}

// ══════════════════════════════════════════════════════════════
//  ЧИСТЫЕ СБОРЩИКИ ИЗ ТЕГОВ
// ══════════════════════════════════════════════════════════════

/** Значение тега из словаря getDICOMTags: {'x00080070': {value}} либо простых пар. */
const зн = (теги, адрес) => {
  const т = теги?.[adrКлюч(адрес)] ?? теги?.[адрес];
  const v = (т && typeof т === 'object') ? т.value : т;
  return String(v ?? '').trim();
};
const adrКлюч = (а) => String(а).toLowerCase();

/** ДД.ММ.ГГГГ из DICOM-даты ГГГГММДД. Чужие форматы возвращаются как есть. */
export function датаПоРусски(dicomДата) {
  const s = String(dicomДата || '').replace(/[^0-9]/g, '');
  if (s.length !== 8) return String(dicomДата || '');
  return `${s.slice(6, 8)}.${s.slice(4, 6)}.${s.slice(0, 4)}`;
}

/**
 * Строка «Аппарат»: производитель, модель и — для МР — напряжённость поля.
 * Образец владельца: «Магнитно-резонансный томограф GE Architect 3,0 T».
 */
export function строкаАппарата(теги, modality) {
  const м = String(modality || зн(теги, 'x00080060')).toUpperCase();
  const производитель = зн(теги, 'x00080070');
  const модель = зн(теги, 'x00081090');
  const поле = зн(теги, 'x00180087');
  const начало = м === 'MR' ? 'Магнитно-резонансный томограф'
    : м === 'CT' ? 'Компьютерный томограф'
    : м === 'PT' ? 'ПЭТ/КТ-сканер' : '';
  const тесла = поле ? `${String(Number(поле).toFixed(1)).replace('.', ',')} Т` : '';
  return [начало, производитель, модель, тесла].filter(Boolean).join(' ').trim();
}

/**
 * «Исследование проведено в ИП: …» — из описаний серий. Дубли схлопываются,
 * порядок сохраняется: врач читает их в том же порядке, что и на аппарате.
 * Производные серии (реформаты, локалайзеры, скриншоты) в перечень не идут.
 */
export function переченьПоследовательностей(серии = []) {
  const мусор = /\b(loc|scout|localizer|report|screen|save|mpr|reformat|dose|patient protocol)\b/i;
  const имена = [];
  for (const с of серии) {
    const имя = String(с?.seriesDesc || с?.desc || '').trim();
    if (!имя || мусор.test(имя)) continue;
    if (!имена.some((и) => и.toLowerCase() === имя.toLowerCase())) имена.push(имя);
  }
  return имена.join(', ');
}

/** Контрастирование: препарат из тега (0018,0010), если аппарат его записал. */
export function строкаКонтраста(теги) {
  const агент = зн(теги, 'x00180010');
  return агент && !/^no$/i.test(агент) ? агент : '';
}

/**
 * Шапка бланка целиком — то, что программа знает сама.
 * @param {object} теги словарь getDICOMTags первого среза
 * @param {Array} серии список серий исследования
 */
export function собратьШапку(теги = {}, серии = []) {
  const modality = зн(теги, 'x00080060') || (серии[0]?.modality || '');
  return {
    фио: зн(теги, 'x00100010'),
    датаРождения: датаПоРусски(зн(теги, 'x00100030')),
    датаИсследования: датаПоРусски(зн(теги, 'x00080020')),
    номерИстории: зн(теги, 'x00080050'),   // AccessionNumber
    аппарат: строкаАппарата(теги, modality),
    последовательности: переченьПоследовательностей(серии),
    контраст: строкаКонтраста(теги),
    modality,
    учреждениеИзТегов: зн(теги, 'x00080080'),
  };
}

export default {
  ПУСТОЙ_БЛАНК, ОТДЕЛЕНИЕ_ПО_УМОЛЧАНИЮ, прочитатьБланк, записатьБланк,
  названиеОтделения, датаПоРусски, строкаАппарата, переченьПоследовательностей,
  строкаКонтраста, собратьШапку, ПОЛЯ_ПО_УМОЛЧАНИЮ, поляБланка,
};


// ══════════════════════════════════════════════════════════════
//  ПРОФИЛИ УЧРЕЖДЕНИЙ [11.1.26]
//  Владелец: «организаций может быть много, каждый раз вводить долго».
//  Профиль — именованный бланк; текущий копируется в активные настройки.
//  Файл обмена — обычный JSON: его можно переслать коллеге или положить в
//  общую папку отделения.
// ══════════════════════════════════════════════════════════════

/** @returns {{имя:string, бланк:object}[]} */
export function прочитатьПрофили() {
  try {
    const с = JSON.parse(localStorage.getItem(КЛЮЧ_ПРОФИЛЕЙ) || '[]');
    return Array.isArray(с) ? с.filter((п) => п && typeof п.имя === 'string') : [];
  } catch (e) { return []; }
}

export function записатьПрофили(список) {
  try { localStorage.setItem(КЛЮЧ_ПРОФИЛЕЙ, JSON.stringify(список || [])); return true; }
  catch (e) { return false; }
}

/** Сохранить бланк под именем (перезапись одноимённого — намеренная). */
export function сохранитьПрофиль(имя, бланк) {
  const чистое = String(имя || '').trim();
  if (!чистое) return { ок: false, причина: 'Пустое имя профиля' };
  const список = прочитатьПрофили().filter((п) => п.имя !== чистое);
  список.push({ имя: чистое, бланк: { ...ПУСТОЙ_БЛАНК, ...бланк } });
  список.sort((a, b) => a.имя.localeCompare(b.имя, 'ru'));
  return записатьПрофили(список) ? { ок: true, всего: список.length } : { ок: false, причина: 'Хранилище недоступно' };
}

export function удалитьПрофиль(имя) {
  const список = прочитатьПрофили().filter((п) => п.имя !== имя);
  return записатьПрофили(список);
}

/**
 * Разбор файла обмена. Принимает ОБА вида: один бланк (объект с полем
 * «учреждение») и пачку профилей ({профили:[…]} либо голый массив) — врач не
 * обязан помнить, что именно он выгружал.
 * @returns {{ок:boolean, профили?:{имя:string,бланк:object}[], причина?:string}}
 */
export function разобратьФайлПрофилей(текст) {
  let д;
  try { д = JSON.parse(String(текст || '')); }
  catch (e) { return { ок: false, причина: 'Это не файл настроек: JSON не читается' }; }
  const нормализовать = (п) => ({ имя: String(п.имя || п.name || 'Без имени').trim(), бланк: { ...ПУСТОЙ_БЛАНК, ...(п.бланк || п.blank || {}) } });
  if (Array.isArray(д)) return { ок: true, профили: д.map(нормализовать) };
  if (Array.isArray(д?.профили)) return { ок: true, профили: д.профили.map(нормализовать) };
  if (д && (д.учреждение || д.реквизиты || д.отделения || д.врачи)) {
    return { ок: true, профили: [{ имя: String(д.имя || 'Импортированный бланк'), бланк: { ...ПУСТОЙ_БЛАНК, ...д } }] };
  }
  return { ок: false, причина: 'В файле нет ни профилей, ни бланка' };
}

/** Пачка профилей файлом — с подписью версии, чтобы потом узнать формат. */
export function собратьФайлПрофилей(профили) {
  return JSON.stringify({ формат: 'mikhmel-report-blanks', версия: 1, профили: профили || прочитатьПрофили() }, null, 2);
}
